// 企業従業員管理 API サービス

import axios, { AxiosResponse } from 'axios';
import {
  CompanyEmployee,
  AddEmployeeFormData,
  UpdateEmployeeFormData,
  EmployeeListFilters,
  EmployeeListResponse,
  AddEmployeeResponse,
  UpdateEmployeeResponse,
  EmployeeErrorResponse,
} from '../types/employee';

class EmployeeService {
  /**
   * APIエラーレスポンスをハンドリング
   */
  private handleAxiosError(error: any): never {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      error.message ||
      'APIエラーが発生しました';
    throw new Error(errorMessage);
  }

  /**
   * 従業員一覧を取得
   */
  async getEmployees(
    companyId: number,
    filters: EmployeeListFilters = {}
  ): Promise<EmployeeListResponse> {
    try {
      const params: any = {};

      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;
      if (filters.status) params.status = filters.status;
      if (filters.sortBy) params.sortBy = filters.sortBy;
      if (filters.sortOrder) params.sortOrder = filters.sortOrder;
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;

      const response = await axios.get(
        `/api/companies/${companyId}/employees`,
        {
          params,
          withCredentials: true, // Cookieベースの認証のため
        }
      );

      return response.data;
    } catch (error) {
      this.handleAxiosError(error);
    }
  }

  /**
   * 従業員を追加
   */
  async addEmployee(
    companyId: number,
    employeeData: AddEmployeeFormData
  ): Promise<AddEmployeeResponse> {
    try {
      const response = await axios.post(
        `/api/companies/${companyId}/employees`,
        employeeData,
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      this.handleAxiosError(error);
    }
  }

  /**
   * 従業員詳細を取得
   */
  async getEmployeeDetail(
    companyId: number,
    employeeId: number
  ): Promise<{ success: boolean; data: CompanyEmployee }> {
    try {
      const response = await axios.get(
        `/api/companies/${companyId}/employees/${employeeId}`,
        {
          withCredentials: true,
        }
      );

      return response.data;
    } catch (error) {
      this.handleAxiosError(error);
    }
  }

  /**
   * 従業員情報を更新
   */
  async updateEmployee(
    companyId: number,
    employeeId: number,
    updateData: UpdateEmployeeFormData
  ): Promise<UpdateEmployeeResponse> {
    try {
      const response = await axios.put(
        `/api/companies/${companyId}/employees/${employeeId}`,
        updateData,
        {
          withCredentials: true,
        }
      );

      return response.data;
    } catch (error) {
      this.handleAxiosError(error);
    }
  }

  /**
   * 有効な企業管理者の数を取得
   * @deprecated 代わりに checkIsLastAdmin を使用してください
   */
  async getActiveAdminCount(companyId: number): Promise<number> {
    try {
      const response = await axios.get(
        `/api/companies/${companyId}/employees`,
        {
          params: {
            role: 'company_admin',
            status: 'active',
            limit: 100, // 十分な数を取得
          },
          withCredentials: true,
        }
      );

      return response.data.data?.employees?.length || 0;
    } catch (error) {
      console.error('企業管理者数の取得に失敗しました:', error);
      return 0;
    }
  }

  /**
   * 最後の企業管理者かどうかを判定（専用エンドポイント使用）
   */
  async checkIsLastAdmin(
    companyId: number,
    employeeId: number
  ): Promise<{ isLastAdmin: boolean; adminCount: number }> {
    try {
      const response = await axios.get(
        `/api/companies/${companyId}/employees/${employeeId}/is-last-admin`,
        {
          withCredentials: true,
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('最後の企業管理者判定に失敗しました:', error);
      // エラー時は安全側に倒す（最後の管理者として扱わない）
      return { isLastAdmin: false, adminCount: 0 };
    }
  }


  /**
   * 従業員の役職ラベルを取得
   * Phase 2: HRロールを追加
   */
  getRoleLabel(role: string): string {
    switch (role) {
      case 'company_admin':
        return '企業管理者';
      case 'hr':
        return '人事担当者';
      case 'employee':
        return '一般従業員';
      default:
        return '不明';
    }
  }

  /**
   * 従業員のステータスラベルを取得
   */
  getStatusLabel(status: string): string {
    switch (status) {
      case 'active':
        return '有効';
      case 'inactive':
        return '無効';
      default:
        return '不明';
    }
  }

  /**
   * 従業員の最終ログイン時刻をフォーマット
   */
  formatLastLogin(lastLoginAt?: string): string {
    if (!lastLoginAt) {
      return 'ログイン履歴なし';
    }

    const date = new Date(lastLoginAt);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return '今日';
    } else if (diffDays === 1) {
      return '昨日';
    } else if (diffDays < 7) {
      return `${diffDays}日前`;
    } else {
      return date.toLocaleDateString('ja-JP');
    }
  }

  /**
   * 従業員の作成日時をフォーマット
   */
  formatCreatedAt(createdAt: string): string {
    const date = new Date(createdAt);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
}

// シングルトンインスタンスをエクスポート
export const employeeService = new EmployeeService();
export default employeeService;
